import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "./shared";

export default function IntegrationsTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // { key, catalog }
    const [testing, setTesting] = useState(null);

    const refresh = () => api.get("/admin/integrations").then((r) => setItems(r.data));
    useEffect(() => { refresh(); }, []);

    const openEditor = async (key) => {
        const r = await api.get(`/admin/integrations/${key}/catalog`);
        setEditing({ key, catalog: r.data });
    };

    const runTest = async (key) => {
        setTesting(key);
        try {
            await api.post(`/admin/integrations/${key}/test`);
            await refresh();
            toast.success("Test complete");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test failed");
        } finally {
            setTesting(null);
        }
    };

    const sendTestEmail = async () => {
        const to = window.prompt("Send a test email to which address?");
        if (!to || !to.trim()) return;
        try {
            const r = await api.post("/admin/integrations/email/send-test", { to_email: to.trim() });
            toast.success(`Test email sent via ${r.data.provider}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed");
        }
    };

    const removeOne = async (key) => {
        if (!window.confirm("Remove these credentials?")) return;
        await api.delete(`/admin/integrations/${key}`);
        await refresh();
        toast.success("Cleared");
    };

    const connectQuickbooks = async () => {
        try {
            const r = await api.post("/admin/quickbooks/authorize", { origin: window.location.origin });
            window.location.href = r.data.authorize_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save QuickBooks Client ID & Secret first.");
        }
    };

    useEffect(() => {
        const qp = new URLSearchParams(window.location.search);
        if (qp.get("qb") === "connected") { toast.success("QuickBooks connected"); refresh(); }
        else if (qp.get("qb") === "error") toast.error("QuickBooks connection failed");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const groups = [
        { id: "payments", label: "Payment gateways", keys: ["stripe", "powertranz", "paypal", "bank_transfer"] },
        { id: "messaging", label: "Messaging", keys: ["mailjet", "sendgrid"] },
        { id: "accounting", label: "Accounting", keys: ["quickbooks"] },
    ];

    return (
        <div data-testid="admin-integrations">
            <PageHeader
                eyebrow="Admin · Settings"
                title="Settings."
                subtitle="Turn integrations on/off and store credentials. Only enabled + configured payment gateways appear on the guest checkout. Secrets are masked once saved; resubmit the masked value to keep the existing secret."
            />
            <div className="space-y-12">
                {groups.map((g) => (
                    <div key={g.id}>
                        <div className="eyebrow mb-4 text-espresso/80">{g.label}</div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {g.keys.map((k) => {
                                const item = items.find((i) => i.key === k);
                                if (!item) return null;
                                return (
                                    <IntegrationCard
                                        key={k}
                                        item={item}
                                        testing={testing === k}
                                        onEdit={() => openEditor(k)}
                                        onTest={() => runTest(k)}
                                        onRemove={() => removeOne(k)}
                                        onConnect={k === "quickbooks" ? connectQuickbooks : null}
                                        onSendTest={(k === "mailjet" || k === "sendgrid") ? sendTestEmail : null}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            {editing && (
                <IntegrationEditor
                    integration={items.find((i) => i.key === editing.key)}
                    catalog={editing.catalog}
                    integrationKey={editing.key}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { await refresh(); setEditing(null); toast.success("Saved"); }}
                />
            )}
        </div>
    );
}

function IntegrationCard({ item, testing, onEdit, onTest, onRemove, onConnect, onSendTest }) {
    const status = !item.configured ? "off" : (item.last_test_ok === true ? "ok" : item.last_test_ok === false ? "fail" : "ready");
    const badge = {
        off: { label: "Not configured", className: "bg-bone2 text-espresso/70" },
        ready: { label: "Configured", className: "bg-gold/15 text-goldDark" },
        ok: { label: "Connected", className: "bg-forest/15 text-forest" },
        fail: { label: "Test failed", className: "bg-terracotta/15 text-terracotta" },
    }[status];
    return (
        <div className="border border-espresso/15 p-6 flex flex-col" data-testid={`integration-card-${item.key}`}>
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <div className="font-serif text-2xl leading-tight">{item.label}</div>
                    <div className="text-[10px] uppercase tracking-widest2 text-muted2 mt-1">{item.key}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest2 px-2 py-1 ${badge.className}`} data-testid={`integration-status-${item.key}`}>{badge.label}</span>
            </div>

            {item.configured && (
                <ul className="text-xs text-muted2 space-y-1 mb-4">
                    {Object.entries(item.config_masked).map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-3">
                            <span className="capitalize">{k.replace(/_/g, " ")}</span>
                            <span className="text-ink font-mono text-[11px]">{v || "—"}</span>
                        </li>
                    ))}
                </ul>
            )}

            {item.last_test_message && (
                <div className={`text-[11px] mb-4 px-3 py-2 border-l-2 ${item.last_test_ok ? "border-forest text-forest" : "border-terracotta text-terracotta"}`} data-testid={`integration-test-msg-${item.key}`}>
                    {item.last_test_message}
                </div>
            )}

            <div className="mt-auto flex items-center justify-between gap-3 pt-3 border-t border-espresso/10">
                <div className="flex gap-3">
                    <button onClick={onEdit} data-testid={`integration-edit-${item.key}`} className="text-xs uppercase tracking-widest2 font-semibold text-forest hover:text-terracotta">
                        {item.configured ? "Update" : "Configure"}
                    </button>
                    {onConnect && item.configured && (
                        <button onClick={onConnect} data-testid={`integration-connect-${item.key}`} className="text-xs uppercase tracking-widest2 font-semibold text-goldDark hover:text-terracotta">
                            {item.last_test_ok ? "Reconnect" : "Connect"}
                        </button>
                    )}
                    {item.configured && (
                        <button onClick={onTest} disabled={testing} data-testid={`integration-test-${item.key}`} className="text-xs uppercase tracking-widest2 text-espresso/80 hover:text-forest disabled:opacity-50">
                            {testing ? "Testing…" : "Test"}
                        </button>
                    )}
                    {onSendTest && item.configured && (
                        <button onClick={onSendTest} data-testid={`integration-sendtest-${item.key}`} className="text-xs uppercase tracking-widest2 text-goldDark hover:text-terracotta">
                            Send test
                        </button>
                    )}
                </div>
                {item.configured && (
                    <button onClick={onRemove} data-testid={`integration-remove-${item.key}`} className="text-[11px] uppercase tracking-widest2 text-terracotta/80 hover:text-terracotta">
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
}

function IntegrationEditor({ integration, catalog, integrationKey, onClose, onSaved }) {
    const initial = {};
    Object.keys(catalog.fields).forEach((f) => {
        initial[f] = integration?.config_masked?.[f] || "";
    });
    const [values, setValues] = useState(initial);
    const [enabled, setEnabled] = useState(integration?.enabled ?? true);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/integrations/${integrationKey}`, { config: values, enabled });
            await onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="integration-editor">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">{catalog.label}</DialogTitle>
                    <DialogDescription>
                        Store credentials for {catalog.label}. Secrets are masked on read — paste a fresh value to update one, or keep the masked value to retain the existing secret.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                    {Object.entries(catalog.fields).map(([name, meta]) => (
                        <div key={name}>
                            <label className="block text-[11px] uppercase tracking-widest2 text-espresso/80 mb-1">
                                {meta.label}{meta.required && <span className="text-terracotta"> *</span>}
                            </label>
                            <input
                                type="text"
                                value={values[name] || ""}
                                onChange={(e) => setValues({ ...values, [name]: e.target.value })}
                                data-testid={`integration-field-${integrationKey}-${name}`}
                                className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent font-mono focus:border-forest outline-none"
                                placeholder={meta.label}
                            />
                        </div>
                    ))}
                    <label className="flex items-center gap-2 text-sm pt-2">
                        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid={`integration-enabled-${integrationKey}`} />
                        Enable this integration
                    </label>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="px-5 py-3 text-xs uppercase tracking-widest2 text-espresso/80 hover:text-forest">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid={`integration-save-${integrationKey}`} className="px-6 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold hover:bg-forestDark disabled:opacity-60">
                        {saving ? "Saving…" : "Save credentials"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
